import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { baseurl } from "../../Baseurl";
import EventBus from "../../Context/EventBus";
import axiosInstance from "../../api/axiosInstance";

export default function AddInsuranceCard({ isOpen, onClose, patientId, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [formData, setFormData] = useState({
    company_id: "",
    policy_number: "",
    issue_date: "",
    expiry_date: "",
    deductible_amount: "",
    co_insurance_percent: "",
    scanned_copy: null,
    claimed: "0"
  });

  // Today's date string for max constraint on issue date (YYYY-MM-DD)
  const todayDateString = new Date().toISOString().split('T')[0];
  // Compute minimum allowed expiry date: cannot be in the past and
  // should not be before the selected issue date
  const minExpiryDate = (formData.issue_date && formData.issue_date > todayDateString)
    ? formData.issue_date
    : todayDateString;

  // Extract a human-friendly error message from API responses
  const getApiErrorMessage = (errorLike, fallback) => {
    try {
      const data = errorLike?.response?.data ?? errorLike;
      const candidates = [];
      if (typeof data === "string") candidates.push(data);
      if (data?.message) candidates.push(data.message);
      if (data?.error) candidates.push(data.error);
      if (Array.isArray(data?.errors)) candidates.push(...data.errors);
      if (data?.errors && typeof data.errors === "object" && !Array.isArray(data.errors)) {
        Object.values(data.errors).forEach((val) => {
          if (Array.isArray(val)) candidates.push(...val);
          else if (val) candidates.push(String(val));
        });
      }
      const msg = candidates.find((m) => m && String(m).trim().length > 0);
      return msg ? String(msg) : (fallback || "Something went wrong");
    } catch (_) {
      return fallback || "Something went wrong";
    }
  };

  // Load insurance companies on component mount
  useEffect(() => {
    if (isOpen) {
      loadInsuranceCompanies();
    }
  }, [isOpen]);

  const loadInsuranceCompanies = async () => {
    try {
      const response = await axiosInstance.get(`getActiveInsuranceCompanies`);
      if (response.data.success) {
        setInsuranceCompanies(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading insurance companies:", error);
      Swal.fire("Error", "Failed to load insurance companies", "error");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      scanned_copy: file
    }));
  };

  // Calculate co-patient percentage based on co-insurance
  const calculateCoPatient = (coInsurance) => {
    if (!coInsurance) return "";
    const coPatient = 100 - parseFloat(coInsurance);
    return coPatient.toString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.company_id) {
      Swal.fire("Required", "Please select an insurance company", "info");
      return;
    }
    if (!formData.policy_number) {
      Swal.fire("Required", "Policy number is required", "info");
      return;
    }
    if (!formData.issue_date) {
      Swal.fire("Required", "Issue date is required", "info");
      return;
    }
    if (!formData.expiry_date) {
      Swal.fire("Required", "Expiry date is required", "info");
      return;
    }
    if (formData.co_insurance_percent === "" || formData.co_insurance_percent === null || formData.co_insurance_percent === undefined) {
      Swal.fire("Required", "Co-patient percentage is required", "info");
      return;
    }

    const coInsurance = parseFloat(formData.co_insurance_percent);
    if (coInsurance < 0 || coInsurance > 100) {
      Swal.fire("Invalid", "Co-insurance percentage must be between 0 and 100", "error");
      return;
    }

    try {
      setLoading(true);

      const formDataToSend = new FormData();
      formDataToSend.append("company_id", formData.company_id);
      formDataToSend.append("patient_id", patientId);
      formDataToSend.append("policy_number", formData.policy_number);
      formDataToSend.append("issue_date", formData.issue_date);
      formDataToSend.append("expiry_date", formData.expiry_date);
      formDataToSend.append("deductible_amount", formData.deductible_amount);
      formDataToSend.append("co_insurance_percent", formData.co_insurance_percent);
      formDataToSend.append("claimed", formData.claimed);

      if (formData.scanned_copy) {
        formDataToSend.append("scanned_copy", formData.scanned_copy);
      }

      const response = await axiosInstance.post(`addInsuranceCard`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        Swal.fire("Success", "Insurance card added successfully", "success");
        onSuccess && onSuccess();
        try { EventBus.dispatch("insurance:cardAdded", { patientId }); } catch (_) { }
        onClose();
        resetForm();
      } else {
        const reason = getApiErrorMessage(response.data, "Failed to add insurance card");
        Swal.fire("Error", reason, "error");
      }
    } catch (error) {
      console.error("Error adding insurance card:", error);
      const reason = getApiErrorMessage(error, "Failed to add insurance card");
      Swal.fire("Error", reason, "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      company_id: "",
      policy_number: "",
      issue_date: "",
      expiry_date: "",
      deductible_amount: "",
      co_insurance_percent: "",
      scanned_copy: null,
      claimed: "0"
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Insurance Card</h5>
         
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Select Company <span className="redAsterisk">*</span></label>
                  <select
                    className="form-select"
                    name="company_id"
                    value={formData.company_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select Insurance Company</option>
                    {insuranceCompanies.map((company) => (
                      <option key={company.company_id} value={company.company_id}>
                        {company.company_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Policy Number <span className="redAsterisk">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    name="policy_number"
                    value={formData.policy_number}
                    onChange={handleInputChange}
                    placeholder="Enter policy number"
                    required
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Issue Date <span className="redAsterisk">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    name="issue_date"
                    value={formData.issue_date}
                    onChange={handleInputChange}
                    max={todayDateString}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Expiry Date <span className="redAsterisk">*</span></label>
                  <input
                    type="date"
                    className="form-control"
                    name="expiry_date"
                    value={formData.expiry_date}
                    onChange={handleInputChange}
                    min={minExpiryDate}
                    required
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <label className="form-label">Deductible Amount (BHD) <span className="redAsterisk">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    name="deductible_amount"
                    value={formData.deductible_amount}
                    onChange={handleInputChange}
                    step="0.001"
                    min="0"
                    placeholder="Enter deductible"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Co-Insurance (%) <span className="redAsterisk">*</span></label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.co_insurance_percent !== "" ? 100 - parseFloat(formData.co_insurance_percent || 0) : ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "") {
                        setFormData(prev => ({ ...prev, co_insurance_percent: "" }));
                      } else {
                        const coIns = 100 - parseFloat(val);
                        setFormData(prev => ({ ...prev, co_insurance_percent: isNaN(coIns) ? "" : String(Math.max(0, Math.min(100, coIns))) }));
                      }
                    }}
                    placeholder="Enter patient %"
                    min="0"
                    max="100"
                    step="0.001"
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Company Share (%)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={formData.co_insurance_percent}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                  <small className="text-muted">Auto-calculated</small>
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">Upload Scanned Copy</label>
                  <input
                    type="file"
                    className="form-control"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                  />
                  {/* <small className="text-muted">Accepted formats: JPG, PNG, PDF</small> */}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="cancelText"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="bgBtn"
                disabled={loading}
              >
                {loading ? "Adding..." : "Add Insurance Card"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
